import React, { useState, useEffect } from 'react';
import { X, Download, Share, Plus } from 'lucide-react';
import { useLogo } from '../App';

const PWAInstallPrompt = () => {
  const { logoUrl } = useLogo();
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // すでにインストール済み（スタンドアロンモード）かチェック
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // iOSかどうかをチェック
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // 過去に閉じたかチェック（24時間は表示しない）
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      if (Date.now() - dismissedTime < 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Androidの場合はbeforeinstallpromptイベントを待つ
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!standalone) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // iOSの場合は3秒後に表示
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('Install prompt outcome:', outcome);
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
      <div className="max-w-md mx-auto bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-4">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-slate-400 hover:text-white"
            aria-label="閉じる"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <img 
                src={logoUrl} 
                alt="PRC勤怠" 
                className="w-14 h-14 rounded-xl object-contain bg-slate-700 p-1"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-lg">PRC勤怠をインストール</h3>
              <p className="text-slate-400 text-sm mt-1">
                ホーム画面に追加して、より快適に打刻できます
              </p>
            </div>
          </div>

          {isIOS ? (
            <div className="mt-4 p-3 bg-slate-700/50 rounded-lg">
              <p className="text-slate-300 text-sm flex items-center gap-2">
                <Share className="w-4 h-4 text-blue-400" />
                下の共有ボタン
                <span className="text-blue-400">↓</span>
                をタップ
              </p>
              <p className="text-slate-300 text-sm flex items-center gap-2 mt-2">
                <Plus className="w-4 h-4 text-blue-400" />
                「ホーム画面に追加」を選択
              </p>
            </div>
          ) : (
            <button
              onClick={handleInstall}
              className="mt-4 w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
            >
              <Download className="w-5 h-5" />
              インストール
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PWAInstallPrompt;
